import { isEmpty } from "lodash";
import { isNil, isNotNil, uniq } from "ramda";
import moment, { MomentInput } from 'moment';
import { Constants } from "./constants";
import {
  addThousandsSeparator,
  ApplicationDownPaymentResponse,
  ApplicationQuoteType, usesFinanceQuote,
  Availability, FinanceLoanClass, FinanceQuote,
  getFullName, getNumberOfPaymentsPerYearForCarletonPaymentFrequency,
  hasValue,
  LinkType,
  PermissionAccessLevel,
  PermissionSet, ApplicationFinanceQuote,
  SimpleTextAndEmailDialogOption,
  toDollars
} from '@amsanalytics/react-shared-library';
import { IEmployee } from "../Interfaces/Admin/IEmployee";
import { IApplication } from "../Interfaces/Application/IApplication";
import { EmployeePermission, ILoggedInEmployee } from "../Interfaces/Admin/ILoggedInEmployee";
import { IState } from "../Interfaces/Common/IState";
import { AlertType } from "../Data/Alert";
import { showAlert, ShowAlertAction } from "../store/Notifications/actions";
import { v4 as uuidv4 } from 'uuid';
import {SET_SERVER_MESSAGE} from "../store/application/types";
import { State } from "@progress/kendo-data-query";
import { IApplicationQueue } from "../Interfaces/Common/IApplicationQueue";
import { KeyFactorThreshold, KeyFactorThresholdSection } from "../Data/KeyFactorThreshold";
import {authService} from "../index";
import {httpGetAsync} from "./HttpClient";
import {getApplicant} from "./appUtils";
import {IAppCustomer} from "../Interfaces/Application/IAppCustomer";
import {
  getIncomeAndDebtBreakdownInternal,
  getSelectedFinanceQuote,
  paymentToIncome
} from "./quoteUtils";
import {IQuote} from "../Interfaces/Common/IQuote";
import {IApplicationLeaseQuote} from "../Interfaces/Application/IApplicationLeaseQuote";
import {IndirectQuoteType} from "../Data/IndirectQuoteType";
import {IQuotePayType} from "../Interfaces/Common/IQuotePayType";
import {getAppQuoteByType} from "../Components/Quoters/IndirectQuoter/IndirectQuoterUtils";

export const getNestedProperty = (theObject: any, path: string) => {
  try {
    const separator = ".";

    return path
      .replace("[", separator)
      .replace("]", "")
      .split(separator)
      .reduce(function (obj, property) {
        return obj[property];
      }, theObject);
  } catch (err) {
    return undefined;
  }
};

export const nullOrNumber = (val: string): number | null => {
  if (val.length === 0) {
    return null;
  }

  const ret = parseFloat(val);
  if (ret === undefined) {
    return null;
  }

  return ret;
};

export const nullOrPositiveNumber = (val: string): number | null => {
  if (val.length === 0) {
    return null;
  }

  const ret = parseFloat(val);
  if (ret === undefined) {
    return null;
  }

  if (ret < 0) {
    return null;
  }

  return ret;
};

export const nullOrPositiveNumberString = (val: string): string => {
  const ret = parseFloat(val);
  if (ret === undefined) {
    return '';
  }

  if (ret < 0) {
    return '';
  }

  return val;
};

export const zeroOrPositiveNumber = (val: string): number => {
  const ret = parseFloat(val);
  if (ret < 0) {
    return 0;
  }

  return ret;
};

export const saveFile = async (response: Response, fallbackFileName: string) => {
  const blob = await response.blob();
  const contentDisposition = response.headers.get('content-disposition');
  let fileName = contentDisposition?.split(';')?.[1].split('filename')?.[1]?.split('=')?.[1]?.trim()
    ?? fallbackFileName;
  // Remove leading/trailing quotes.
  if (fileName.startsWith('"')) {
    fileName = fileName.substring(1);
  }
  if (fileName.endsWith('"')) {
    fileName = fileName.substring(0, fileName.length - 1);
  }

  if ((window.navigator as any).msSaveOrOpenBlob) {
    (window.navigator as any).msSaveOrOpenBlob(blob, fileName);
  } else {
    const anchor = window.document.createElement('a');
    anchor.href = window.URL.createObjectURL(blob);
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(anchor.href);
  }
}

export const toFixedNumber = (num?: number, digits?: number, base?: number) => {
  if (num) {
    const pow = Math.pow(base || 10, digits || 2);
    const result = Math.round(num * pow) / pow;
    return result || undefined;
  } else {
    return undefined;
  }
}

// a little function to help us with reordering the result
export const reorder = (list: any[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

// Returns true if the given value is a positive number (or a stringified positive number), otherwise false.
export const isPositiveNumber = (value: string | number | undefined): boolean => {
  if (isNil(value)) {
    return false;
  }
  if (typeof value === 'string') {
    return isEmpty(value.trim()) ? false : +value > 0;
  }
  if (typeof value === 'number') {
    return value > 0;
  }
  return true;
}

export const formatDateToString = (date: string | Date | null): string => {
  if (isNil(date)) {
    return '';
  } else {
    return new Intl.DateTimeFormat('en-US',
      { year: 'numeric', month: 'long', day: '2-digit' })
      .format(typeof date === 'string' ? new Date(date) : date);
  }
}
export const dateIsBeforeDate = (dateInput1: MomentInput, dateInput2: MomentInput): boolean => {
  const date1 = moment(dateInput1);
  const date2 = moment(dateInput2);
  return date1.isBefore(date2);
};

export const dateDiffStringAsNumDays = (dateString1: string, dateString2: string): number => {
  const date1 = moment(dateString1);
  const date2 = moment(dateString2);
  return date1.diff(date2, 'days');
}

export const dateDiffStringAsTotalTime = (dateString1: string, dateString2: string): string => {
  const date1 = moment(dateString1);
  const date2 = moment(dateString2);
  const minutes = date1.diff(date2, 'minutes');
  if (minutes > 60) {
    const hours = date1.diff(date2, 'hours');
    if (hours > 24) {
      const days = date1.diff(date2, 'days');
      return `${days} days`
    }
    return `${hours} hrs`;
  }
  if (minutes < 0) {
    return '0'
  }
  return `${minutes} mins`;
}

export const getOrdinalNumber = (num: number) => {
  switch (num) {
    case 1:
      return 'First ';
    case 2:
      return 'Second ';
    case 3:
      return 'Third ';
    default:
      return '';
  }
};

export const getYearsAndMonthsBetweenDates = (startDate: Date, endDate: Date) => {
  const sDate = moment(startDate);
  const eDate = moment(endDate);

  const totalMonths = eDate.diff(sDate, 'months');

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  return [years, months];
};

export const getDaysBetweenDates = (startDate: Date, endDate: Date) => {
  const sDate = moment(startDate);
  const eDate = moment(endDate);

  return eDate.diff(sDate, 'days');
};

export const getAvailabilityStatusId = (availability: Availability): string | null => {
  return availability === Availability.Available
    ? Constants.availability.available
    : availability === Availability.Away
      ? Constants.availability.away
      : availability === Availability.DoNotDisturb
        ? Constants.availability.doNotDisturb
        : null;
}

export const getAvailabilityEnumValue = (availabilityStatusId: string): Availability => {
  return availabilityStatusId === Constants.availability.available
    ? Availability.Available
    : availabilityStatusId === Constants.availability.away
      ? Availability.Away
      : availabilityStatusId === Constants.availability.doNotDisturb
        ? Availability.DoNotDisturb
        : Availability.Away;
}
// Calculates the numerical average of the provided list.
export const getAverage = (items: number[]): number => {
  return items.reduce((a, b) => a + b) / items.length;
}

export const getEmployeeName = (employees: IEmployee[], employeeId: string | undefined): string => {
  if (employeeId === Constants.systemUserId) {
    return 'System';
  }

  const employee = employees.find(e => e.id === employeeId);
  return getFullName(employee, "Unknown");
}

// Returns the given date in milliseconds.
export const getTimeInMillis = (date?: Date) => {
  return !isNil(date) ? date.getTime() : 0;
}

// Returns the appropriate role name for the given customer role ID.
export const getNameForCustomerRole = (customerRoleId: string): string => {
  if (customerRoleId.toUpperCase() === Constants.roleApplicantId) {
    return 'Applicant';
  }
  if (customerRoleId.toUpperCase() === Constants.roleCoApplicant1Id) {
    return 'Co-Applicant 1';
  }
  return 'Co-Applicant 2';
};

export const toPercent = (value: number | undefined): string => {
  return Intl.NumberFormat('en-US', { style: "percent", maximumFractionDigits: 2 })
    .format(value ?? 0);
}
export const toDecimal = (value: number): string => {
  return toDollars(value).replace('$', '');
}

export const formatValue = (value: number | undefined, format: 'decimal' | 'money' | 'percent' | 'number') => {
  if (value === undefined) return '';
  if (format === "decimal") {
    return toDecimal(value);
  } else if (format === 'money') {
    return toDollars(value);
  } else if (format === 'percent') {
    return toPercent(value);
  } else { // number
    return addThousandsSeparator(value);
  }
}
export const removeTimeFromDateTimeString = (dateTime: string): string => {
  if (dateTime.indexOf(',') > -1) {
    return dateTime.split(',')[0];
  }
  if (dateTime.indexOf('T') > -1) {
    return dateTime.split('T')[0];
  }
  return dateTime;
}

export const formatDateOrTimeString = (dateString: string): string => {
  const date = moment(dateString);
  var date1 = moment(date).format('YYYY-MM-DD');
  var date2 = moment(new Date()).format('YYYY-MM-DD');

  // If the date was not today just return the date
  if (date1 < date2) {
    return date.format('MM/DD/YY');
  }
  // Else if it's today return just the time
  else {
    return date.format('hh:mm A');
  }
};

// Formats the time in 12-hour format with AM/PM.
export const formatTime12HourShort = (timestamp: string) => {
  if (isNil(timestamp)) {
    return null;
  }
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(date);
};

// Returns true if the current application has at least one customer with existing customer info.
// And at least one of the applicant's SSN isn't '000-00-0000'
export const hasExistingCustomerInformation = (currentApp: IApplication | undefined): boolean => {
  return currentApp?.appCustomers?.some(ac => ac.customer.existingCustomerInfo.length > 0 && ssnIsNotAllZeroes(ac.customer.ssn)) || false;
}

export const ssnIsNotAllZeroes = (ssn: string): boolean => {
  return ssn?.replaceAll('-', '') !== '000000000';
}

// Returns true if the logged in employee has permissions to access a given item, otherwise false.
export const userHasPermission = (employee: ILoggedInEmployee, permission: string, permissionType: PermissionSet, accessLevel?: PermissionAccessLevel): boolean => {
  const employeePermissions: EmployeePermission[] = employee.employeePermissions
    .filter(ep => ep.id.toLowerCase() === permission.toLowerCase());

  let validEmployeePermissions: EmployeePermission[] = [];

  if (employeePermissions.length > 0) {
    switch (permissionType) {
      case PermissionSet.C:
        validEmployeePermissions = employeePermissions.filter(ep => ep.canCreate);
        if (validEmployeePermissions.length === 0) {
          return false;
        }
        break;
      case PermissionSet.R:
        validEmployeePermissions = employeePermissions.filter(ep => ep.canView);
        if (validEmployeePermissions.length === 0) {
          return false;
        }
        break;
      case PermissionSet.U:
        validEmployeePermissions = employeePermissions.filter(ep => ep.canUpdate);
        if (validEmployeePermissions.length === 0) {
          return false;
        }
        break;
      case PermissionSet.D:
        validEmployeePermissions = employeePermissions.filter(ep => ep.canDelete);
        if (validEmployeePermissions.length === 0) {
          return false;
        }
        break;
      default:
        break;
    }

    return accessLevel
      ? validEmployeePermissions.find(ep => ep.permissionAccessLevel <= accessLevel) !== undefined
      : true;
  }

  return false;
};

export const capitalizeFirstLetter = (word: string): string => {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export const splitCamelCaseWord = (word: string): string => {
  return word.trim().split(/(?=[A-Z])/).join(' ');
}

// Removes all hyphens from the given string.
export const removeHyphensFromString = (word: string): string => {
  return word.replace(/-/g, "");
}

// Returns the given state that matches the abbreviation, otherwise returns undefined. Case insensitive.
export const getStateByAbbreviation = (abbreviation: string, states: IState[]): IState | undefined => {
  return states.find((s: IState) => s.abbreviation.toLowerCase() === abbreviation.toLowerCase());
}

// returns true if the two strings are equal, otherwise returns false. Case insensitive, trim whitespace.
export const areStringsEqual = (str1: string | null | undefined, str2: string | null | undefined): boolean => {
  // if both are null or undefined, return true
  if (isNil(str1) && isNil(str2)) {
    return true;
  }
  return str1?.trim()?.toUpperCase() === str2?.trim()?.toUpperCase();
}

export const capitalizeFirstLetterOfEachWord = (word: string): string => {
  if (!word || !hasValue(word)) {
    return '';
  }
  return word.toLowerCase()
    .split(' ')
    .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
    .join(' ');
};

export const numberWithCommas = (num: number | null | undefined): string | null => {
  if (isNil(num)) {
    return null;
  }
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export const displayMessage = (message: string, type: AlertType): ShowAlertAction => {
  return showAlert({
    type: type,
    message,
    id: uuidv4(),
  });
};

export const displayError = (error: string) => {
  return { type: SET_SERVER_MESSAGE, payload: error, isError: true };
}

// returns a list of the menu options for the "Generate Portal Link" button. For all appCustomers, show option to generate link
// based on whether or not the appCustomer has an email or phone number.
export const getCustomerPortalLinkMenuOptions = (currentApp: IApplication | undefined): SimpleTextAndEmailDialogOption[] => {
  // List of menu options to return.
  const menuOptions: SimpleTextAndEmailDialogOption[] = [];
  // First, add options for each application customer. This is because the salesperson can generate portal links for co-applicants
  currentApp?.appCustomers?.forEach((appCustomer) => {
    // add email and/or phone options for this customer
    appCustomer.customer.customerEmail.filter(customerEmail => !customerEmail.doNotEmail)
      .forEach(customerEmail => {
        menuOptions.push({
          type: 'EmailAddress',
          to: customerEmail.emailAddress,
          recipientName: getFullName(appCustomer.customer, 'Unknown')
        });
      });
    appCustomer.customer.customerPhone.filter(customerPhone => !customerPhone.doNotText && ["Mobile", "Cell"].includes(customerPhone.type))
      .forEach(customerPhone => {
        menuOptions.push({
          type: 'PhoneNumber',
          to: customerPhone.phoneNumber,
          recipientName: getFullName(appCustomer.customer, 'Unknown')
        });
      });
  });
  return menuOptions;
}

// returns a list of the menu options for the "Generate Portal Link" button. For all appCustomers, show option to generate link
// based on whether or not the appCustomer has an email or phone number.
export const getCustomerPortalLinkMenuOptionsByRole = (currentApp: IApplication | undefined, linkType: LinkType): SimpleTextAndEmailDialogOption[] => {
  // List of menu options to return.
  const menuOptions: SimpleTextAndEmailDialogOption[] = [];
  let appCustomerRole = Constants.roleApplicantId;
  if (linkType == LinkType.bankVerificationLinkCoApplicant) {
    appCustomerRole = Constants.roleCoApplicant1Id;
  }

  const appCustomer = currentApp?.appCustomers?.find(ac => ac.customerRoleId.toUpperCase() === appCustomerRole);

  // First, add options for each application customer. This is because the salesperson can generate portal links for co-applicants
  //currentApp?.appCustomers?.forEach((appCustomer) => {
  if (appCustomer) {
    // add email and/or phone options for this customer
    appCustomer.customer.customerEmail.filter(customerEmail => !customerEmail.doNotEmail)
      .forEach(customerEmail => {
        menuOptions.push({
          type: 'EmailAddress',
          to: customerEmail.emailAddress,
          recipientName: getFullName(appCustomer.customer, 'Unknown')
        });
      });

    appCustomer.customer.customerPhone.filter(customerPhone => !customerPhone.doNotText && ["Mobile", "Cell"].includes(customerPhone.type))
      .forEach(customerPhone => {
        menuOptions.push({
          type: 'PhoneNumber',
          to: customerPhone.phoneNumber,
          recipientName: getFullName(appCustomer.customer, 'Unknown') 
        });
      });
  };

  return menuOptions;
}

export const getNamesOfCustomersThatNeedAHardPull = (currentApp: IApplication): string[] => {
  // If credit was pulled
  // The record is new enough to have a value in CustomerCredit.HardPull
  // AND it was pulled with a soft pull, prompt the user to go back and do a hard pull before desking
  const customersThatNeedHardPullToDesk = currentApp.appCustomers.filter(ac =>
    ac.customer.customerCredit.some(cc => isNotNil(cc.hardPull) && !cc.hardPull))
    .map(ac => ac.customer);

  return uniq(customersThatNeedHardPullToDesk.map(c => getFullName(c)));
}


export const getStringValue = (value: any): string => {
  return value ? value.toString() : '';
}

// returns true if the given string is a GUID based on its length, otherwise returns false.
export const isGuid = (guid: string): boolean => {
  return guid.trim().length === 36;
}

export const getDefaultApplicationQueueSort = (applicationQueue: IApplicationQueue): State => {
  if (applicationQueue.applicationQueueDefaultSortings.length > 0) {
    return { skip: 0, take: 20, sort: applicationQueue.applicationQueueDefaultSortings.map(ds => { return { field: ds.fieldName, dir: ds.direction } }) };
  }

  return { skip: 0, take: 20, sort: [{ field: "applicationNo", dir: "desc" }] };
}

// Excludes the time, only cares about the month, day, and year
export const isSameDate = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

// Get name from value
export const getEnumName = <T extends Record<string, string | number>>(enumObj: T, value: string | number): string | undefined =>
  Object.keys(enumObj).find(key => enumObj[key] === value);

// Gets the KeyFactorThresholdSection for a given value and KeyFactorThreshold so we can determine if the value is in the 'bad', 'warning', or 'good' range.
export const getThresholdSectionForValue = (value: string | number, keyFactorThreshold: KeyFactorThreshold): KeyFactorThresholdSection => {
  // Direction: 1 - Higher is better, 2 - Lower is better
  return keyFactorThreshold.direction === '1' ?
    +value >= +keyFactorThreshold.minGoodThreshold ? KeyFactorThresholdSection.Good :
      +value >= +keyFactorThreshold.minWarnThreshold ? KeyFactorThresholdSection.Warn : KeyFactorThresholdSection.Bad :
    +value <= +keyFactorThreshold.minGoodThreshold ? KeyFactorThresholdSection.Good :
      +value <= +keyFactorThreshold.minWarnThreshold ? KeyFactorThresholdSection.Warn : KeyFactorThresholdSection.Bad;
}

export const getGeneratedStockNumberAsync = async (): Promise<string | undefined> => {
  const bearerToken = await authService.getToken();
  const stockNumber: Response | undefined = await httpGetAsync('api/Vehicle/generateStockNumber', bearerToken!);
  return stockNumber?.json();
}

export const getTotalDownPaymentAmountPaid = (applicationDownPaymentResponses: ApplicationDownPaymentResponse[] | undefined): number | undefined => {
  return applicationDownPaymentResponses?.reduce((sum, payment) => sum + payment.amount, 0);
}

const getRepossessionsForAppCustomer = (appCustomer: IAppCustomer | undefined): number | string => {
  if (!appCustomer) {
    return 0;
  }
  
  return appCustomer.customer.customerCustomFields.find(cf => cf.customField.customerCreditOverrideField?.toLowerCase() === 'numberofrepossessions')?.value
    ?? appCustomer.customer.customerCredit[0]?.numberOfRepossessions;
}

export const getTotalRepossessions = (currentApp: IApplication | undefined): number => {
  if (!currentApp) {
    return 0;
  }
  
  const primary = getApplicant(currentApp, Constants.roleApplicantId, true);
  const coApp1 = getApplicant(currentApp, Constants.roleCoApplicant1Id, true);
  const coApp2 = getApplicant(currentApp, Constants.roleCoApplicant2Id, true);

  const reposForPrimary = getRepossessionsForAppCustomer(primary);
  const reposForCoApp1 = getRepossessionsForAppCustomer(coApp1);
  const reposForCoApp2 = getRepossessionsForAppCustomer(coApp2);
  
  return (reposForPrimary ? +reposForPrimary : 0) + (reposForCoApp1 ? +reposForCoApp1 : 0) + (reposForCoApp2 ? +reposForCoApp2 : 0);
}

interface PaymentToIncomeRatioWithPayment {
  paymentToIncomeRatio: number;
  payment: number;
}

export const getPaymentToIncomeRatioWithPayment = (currentApp: IApplication | undefined, quotePayTypes: IQuotePayType[]): PaymentToIncomeRatioWithPayment => {
  if (!currentApp) {
    return {
      paymentToIncomeRatio: 0,
      payment: 0
    }
  }

  const underwriterQuote = (currentApp.quoteType === ApplicationQuoteType.Indirect)
    ? getAppQuoteByType(currentApp, IndirectQuoteType.Underwriter)
    : undefined;

  const selectedFinanceQuote = usesFinanceQuote(currentApp.quoteType)
    ? getSelectedFinanceQuote(currentApp)
    : undefined;

  const leaseQuote = currentApp.quoteType === ApplicationQuoteType.Lease
    ? currentApp.applicationLeaseQuote
    : undefined;
  
  const quoteToUseForCalculations: IQuote | IApplicationLeaseQuote | ApplicationFinanceQuote = selectedFinanceQuote
    ?? currentApp.applicationLeaseQuote
    ?? underwriterQuote;
  const selectedAmsPayTypePaymentsPerYear = usesFinanceQuote(currentApp.quoteType) && quoteToUseForCalculations
    ? getNumberOfPaymentsPerYearForCarletonPaymentFrequency((quoteToUseForCalculations as FinanceQuote).paymentFrequency)
    : quotePayTypes.find(qpt => qpt.id?.toLowerCase() === (quoteToUseForCalculations as IApplicationLeaseQuote)?.paymentFrequencyId?.toLowerCase())?.paymentsPerYear ?? 12;
  const incomeRows = currentApp.applicationBudget?.incomeRows;
  
  const incomeAndDebtInfo = getIncomeAndDebtBreakdownInternal(
    currentApp.applicationBudget, currentApp.appCustomers || [],
    undefined,
    currentApp.includeAllCurrentIncomes,
    incomeRows?.find(r => r.name === 'Additional Income')?.isAutomaticallyCalculated ?? false);

  const payment = selectedFinanceQuote?.payment
    ?? leaseQuote?.fullPaymentAmount
    ?? underwriterQuote?.quote.payment
    ?? 0;
  
  const paymentToIncomeRatio = paymentToIncome(payment ?? 0, selectedAmsPayTypePaymentsPerYear, incomeAndDebtInfo.totalIncomeUsed);
  
  return {
    paymentToIncomeRatio: paymentToIncomeRatio,
    payment: payment
  };
};

export const isValidDate = (value: any): boolean => {
  if (!value) return false;
  
  // If it's already a Date object, check if it's valid
  if (value instanceof Date) {
    return !isNaN(value.getTime());
  }
  
  // For strings or other types, use moment to parse and validate
  return moment(value).isValid();
};

export const getFeeAmountForCarletonInsuranceCoverageType = (
  financeLoanClass: FinanceLoanClass | undefined, 
  financeQuote: FinanceQuote,
  carletonInsuranceCoverageType: string
): number => {
  const financeLoanClassFinanceFeeForCoverage =financeLoanClass?.financeLoanClassFinanceFees
    .find(flcff => flcff.carletonInsuranceCoverageTypeId === carletonInsuranceCoverageType);
  if (!financeLoanClassFinanceFeeForCoverage) {
    return 0;
  }

  const feeForCoverage = financeQuote.financeQuoteFees
    .find(afqf => afqf.financeLoanClassFinanceFeeId === financeLoanClassFinanceFeeForCoverage.id);
  return feeForCoverage?.amount ?? 0;
}